import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

class EventAPI {
  constructor() {
    this.baseURL = `${API_BASE_URL}/api/v1/events`;
    this.axiosInstance = axios.create({
      baseURL: this.baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor for authentication
    this.axiosInstance.interceptors.request.use(
      (config) => {
        // Get the admin token from localStorage (stored by AuthContext)
        const token = localStorage.getItem('travel-futura-token');
        
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('travel-futura-token');
          localStorage.removeItem('travel-futura-user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Get all events with optional filters
  async getAllEvents(params = {}) {
    try {
      const response = await this.axiosInstance.get('/', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching events:', error);
      throw error;
    }
  }

  // Get event by ID
  async getEventById(id, language = 'en') {
    try {
      const response = await this.axiosInstance.get(`/${id}`, {
        params: { language }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching event:', error);
      throw error;
    }
  }

  // Create new event
  async createEvent(eventData) {
    try {
      const response = await this.axiosInstance.post('/', eventData);
      return response.data;
    } catch (error) {
      console.error('Error creating event:', error);
      throw error;
    }
  }

  // Update event
  async updateEvent(id, eventData) {
    try {
      const response = await this.axiosInstance.put(`/${id}`, eventData);
      return response.data;
    } catch (error) {
      console.error('Error updating event:', error);
      throw error;
    }
  }

  // Delete event
  async deleteEvent(id) {
    try {
      const response = await this.axiosInstance.delete(`/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting event:', error);
      throw error;
    }
  }

  // Get events by time filter (past, current, upcoming)
  async getEventsByTimeFilter(filter, params = {}) {
    try {
      const response = await this.axiosInstance.get(`/filter/time/${filter}`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching events by time filter:', error);
      throw error;
    }
  }

  // Get events by category
  async getEventsByCategory(category, params = {}) {
    try {
      const response = await this.axiosInstance.get(`/filter/category/${category}`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching events by category:', error);
      throw error;
    }
  }

  // Get featured events
  async getFeaturedEvents(limit = 5) {
    try {
      const response = await this.axiosInstance.get('/featured/list', {
        params: { limit }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching featured events:', error);
      throw error;
    }
  }

  // Get top events
  async getTopEvents(limit = 5) {
    try {
      const response = await this.axiosInstance.get('/top/list', {
        params: { limit }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching top events:', error);
      throw error;
    }
  }

  // Search events
  async searchEvents(query, params = {}) {
    try {
      const response = await this.axiosInstance.get(`/search/${encodeURIComponent(query)}`, { params });
      return response.data;
    } catch (error) {
      console.error('Error searching events:', error);
      throw error;
    }
  }

  // Get events by date range
  async getEventsByDateRange(startDate, endDate, params = {}) {
    try {
      const response = await this.axiosInstance.get(`/filter/date/${startDate}/${endDate}`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching events by date range:', error);
      throw error;
    }
  }

  // Update event status
  async updateEventStatus(id, status) {
    try {
      const response = await this.axiosInstance.patch(`/${id}/status`, { status });
      return response.data;
    } catch (error) {
      console.error('Error updating event status:', error);
      throw error;
    }
  }

  // Toggle featured status
  async toggleFeatured(id) {
    try {
      const response = await this.axiosInstance.patch(`/${id}/featured`);
      return response.data;
    } catch (error) {
      console.error('Error toggling featured status:', error);
      throw error;
    }
  }

  // Toggle top event status
  async toggleTopEvent(id) {
    try {
      const response = await this.axiosInstance.patch(`/${id}/top`);
      return response.data;
    } catch (error) {
      console.error('Error toggling top event status:', error);
      throw error;
    }
  }

  // Archive event (Admin only)
  async archiveEvent(id) {
    try {
      const response = await this.axiosInstance.patch(`/${id}/archive`);
      return response.data;
    } catch (error) {
      console.error('Error archiving event:', error);
      throw error;
    }
  }

  // Unarchive event (Admin only)
  async unarchiveEvent(id) {
    try {
      const response = await this.axiosInstance.patch(`/${id}/unarchive`);
      return response.data;
    } catch (error) {
      console.error('Error unarchiving event:', error);
      throw error;
    }
  }

  // Archive all past events (Admin only) - run automatically when Event Management loads
  async archivePastEvents() {
    try {
      const response = await this.axiosInstance.post('/admin/archive-past');
      return response.data;
    } catch (error) {
      console.error('Error archiving past events:', error);
      throw error;
    }
  }

  // Get event statistics (Admin only)
  async getEventStatistics() {
    try {
      const response = await this.axiosInstance.get('/admin/statistics');
      return response.data;
    } catch (error) {
      console.error('Error fetching event statistics:', error);
      throw error;
    }
  }

  // Upload event banner image
  async uploadBannerImage(file) {
    try {
      const formData = new FormData();
      formData.append('image', file);
      
      // Use single image upload endpoint for cleaner response
      const response = await axios.post(`${API_BASE_URL}/api/v1/upload/image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('travel-futura-token')}`
        },
        timeout: 60000
      });
      
      return response.data;
    } catch (error) {
      console.error('Error uploading banner image:', error);
      throw error;
    }
  }

  // Get upcoming events for banner management
  async getUpcomingEventsForBanner(limit = 10) {
    try {
      const response = await this.axiosInstance.get('/filter/time/upcoming', {
        params: { 
          limit,
          status: 'published',
          sort_by: 'start_date',
          sort_order: 'ASC'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching upcoming events for banner:', error);
      throw error;
    }
  }

  // Get current events for banner management
  async getCurrentEventsForBanner(limit = 10) {
    try {
      const response = await this.axiosInstance.get('/filter/time/current', {
        params: { 
          limit,
          status: 'published',
          sort_by: 'start_date',
          sort_order: 'ASC'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching current events for banner:', error);
      throw error;
    }
  }

  // Get featured events for banner management
  async getFeaturedEventsForBanner(limit = 10) {
    try {
      const response = await this.axiosInstance.get('/featured/list', {
        params: { 
          limit,
          status: 'published'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching featured events for banner:', error);
      throw error;
    }
  }
}

export const eventAPI = new EventAPI();
export default eventAPI;
